/**
 * bootSplash.ts
 * src/shared/utils/bootSplash.ts
 *
 * Controls the static launch splash that lives in index.html.
 *
 * WHY THIS EXISTS
 * ───────────────
 * The splash used to sit INSIDE #root, so React wiped it the moment it
 * mounted. That forced a handoff to a second, React-rendered green
 * screen (LukaLoadingScreen), and the two were never quite identical -
 * the logo shifted between them on every cold open.
 *
 * The splash is now a SIBLING of #root, so React never touches it. It
 * paints on the first frame and stays put until the app says it is
 * genuinely ready. One screen for the whole launch. There is no handoff
 * left to get wrong.
 *
 * HOW IT IS DISMISSED
 * ───────────────────
 * Whoever knows the app is ready calls dismissBootSplash(). Today that
 * is AppShell (once the user profile has loaded) and Router (once it
 * has settled on the auth or onboarding view). The call is idempotent -
 * calling it twice, or before the element exists, is harmless.
 *
 * THE SAFETY NET
 * ──────────────
 * A splash that is never dismissed would hide the entire app, so this
 * module arms a hard timeout at import. If nothing has dismissed it
 * within MAX_LIFETIME_MS, it removes itself anyway and the user sees
 * whatever the app managed to render. A visibly broken app beats an
 * app you cannot reach. If you ever see the splash sit for the full
 * timeout, something upstream failed - that is a bug to chase, not a
 * timeout to lengthen.
 */

const SPLASH_ID = 'boot-splash'

// Must match the transition duration on #boot-splash in index.html.
const FADE_MS = 350

// Hard ceiling. Long enough that a slow cold start on a poor connection
// will not trip it, short enough that a genuine failure does not leave
// someone staring at a green screen.
const MAX_LIFETIME_MS = 10000

let dismissed = false

/**
 * Fade the launch splash out and remove it from the DOM.
 * Safe to call any number of times, from anywhere, at any point.
 */
export function dismissBootSplash(): void {
  if (dismissed) return
  dismissed = true

  const el = document.getElementById(SPLASH_ID)
  if (!el) return

  el.classList.add('is-dismissed')
  window.setTimeout(() => el.remove(), FADE_MS)
}

// Arm the safety net as soon as this module is imported.
if (typeof window !== 'undefined') {
  window.setTimeout(() => {
    if (!dismissed) {
      console.warn(
        '[bootSplash] Nothing dismissed the launch splash within ' +
        `${MAX_LIFETIME_MS}ms. Removing it so the app is reachable. ` +
        'Something upstream did not report ready - worth investigating.'
      )
      dismissBootSplash()
    }
  }, MAX_LIFETIME_MS)
}
